import * as React from 'react';
import PopupWrapper from '../../../components/Popup/PopupWrapper';
import { List, AutoSizer } from 'react-virtualized/dist/commonjs';
import PopupHeader from '../../../components/Popup/PopupHeader';
import PopupBody from '../../../components/Popup/PopupBody';
import * as moment from 'moment';
import * as _ from 'lodash';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Loader, LoaderSize, LoaderTheme } from '../../../components/Loader/Loader';
import Formatter from '../../../../services/Formatter';
import TaskCodeViewer from './TaskCodeViewer/TaskCodeViewer';
import { Button, ButtonIcons, ButtonSize, ButtonTheme } from '../../../components/Button/Button';
import PopupHeaderRightPart from '../../../components/Popup/PopupHeaderRightPart';
import { useClosePopup } from '../../../../hooks/useClosePopup';
import { useCrawlerTasks } from './hooks/useCrawlerTasks';
import { IProject } from '../../../../models/IProject';
import { IHighlightLineParams } from '../../../../redux/scriptEditor/actions.scriptEditor';
import ConfirmPopup2 from '../../../components/ConfirmPopup/ConfirmPopup2';
import formatFileLine from '../../../../services/mini/formatFileLine';
import getUrlInfo from '../../../../services/mini/getUrlInfo';
import { FlexContainer } from '../../../components/FlexContainer/FlexContainer';
import { createPortal } from 'react-dom';
import CorpusPage from '../../corpuse/CorpusPage/CorpusPage';
import { ICorpusTask } from '../../../../models/ICorpusTask';
import { getCorpusTitleFromTask } from '../../../../services/mini/getCorpusTitleFromTask';
import TopLevelOverlay from '../../../components/TopLevelOverlay/TopLevelOverlay';

const ROW_HEIGHT = 150;

const formatTs = (ts) => {
    if (!ts) return '-';

    const date = moment(+ts);
    const now = moment();

    if (date.isSame(now, 'day')) {
        return date.format('h:mm:ss A');
    } else if (date.isSame(now, 'year')) {
        return date.format('h:mm:ss A,<br>MMM D');
    } else {
        return date.format('h:mm:ss A,<br>MMM D, YY');
    }
}

function getTimeDiff(startTs, endTs) {
    if (!+startTs || !+endTs) return '-';
    return Formatter.getTimeDiff(startTs, endTs)
}

function isTaskCanBeStopped(statusMessage) {
    return statusMessage !== TaskStatusMesage.Complete &&
        statusMessage !== TaskStatusMesage.Interrupted &&
        statusMessage !== TaskStatusMesage.Error;
}

export enum TaskStatusMesage {
    Complete = 'complete',
    Started = 'started',
    Error = 'error',
    Interrupted = 'interrupted',
}

type TaskRowProps = {
    task: ICorpusTask,
    index: number,
    highlightLine: (p: IHighlightLineParams) => void,
    project: IProject,
    onClose: () => void,
    onDelete: (task: ICorpusTask) => void,
    onStop: (task: ICorpusTask) => void,
    onRestart: (task: ICorpusTask) => void,
    onBrowse: (task: ICorpusTask) => void,
}

function TaskRow({ task, index, highlightLine, project, onClose, onDelete, onStop, onRestart, onBrowse }: TaskRowProps) {
    const { projectName, scriptName } = getUrlInfo();

    const { uniqueKey, data, statusMessage, createTs, completeTs, errorTs, takenCount } = task;

    let dataObj;

    try {
        dataObj = JSON.parse(data);
    } catch (error) {

    }

    const { location } = dataObj || {};

    const handleGoTo = () => {
        if (location?.file && location?.line) {
            onClose();
            highlightLine({
                scriptName: location?.file,
                lineNumber: location?.line,
                character: 0,
                project
            });
        }
    }

    const handleGoToCorpusCb = useCallback(() => {
        onBrowse(task)
    }, [onBrowse, task]);

    const handleStop = useCallback(() => {
        onStop(task);
    }, [onStop, task])

    const handleRestart = useCallback(() => {
        onRestart(task);
    }, [onRestart, task])

    const handleDelete = useCallback(() => {
        onDelete(task);
    }, [onDelete, task]);

    const status = statusMessage?.toLocaleLowerCase().includes('error') || statusMessage?.length > 50 ?
        TaskStatusMesage.Error : statusMessage;

    return <div key={uniqueKey} className="crawler-inspector-popup__table-row">
        <div className="crawler-inspector-popup__table-body-cell text-centered">{index + 1}</div>
        <div className="crawler-inspector-popup__table-body-cell text-centered"
            dangerouslySetInnerHTML={{ __html: formatTs(createTs) }}>
        </div>
        <div className="crawler-inspector-popup__table-body-cell text-centered ">
            <span className={`task-status status-${status}`} title={statusMessage}>
                {statusMessage}
                {(status === TaskStatusMesage.Error && errorTs) &&
                    <>
                        <br />
                        <span style={{ fontSize: '11px' }} dangerouslySetInnerHTML={{ __html: formatTs(errorTs) }}>
                        </span>
                    </>}
            </span>
        </div>
        <div className="crawler-inspector-popup__table-body-cell text-centered"
            dangerouslySetInnerHTML={{ __html: formatTs(completeTs) }}>
        </div>
        <div className="crawler-inspector-popup__table-body-cell text-centered ">{getTimeDiff(createTs, completeTs)}</div>
        <div className="crawler-inspector-popup__table-body-cell text-centered ">{takenCount}</div>
        <div className={`crawler-inspector-popup__table-body-cell no-padding`}>
            <TaskCodeViewer
                code={dataObj?.code || 'No code to display'}
            />
            <div className="crawler-inspector-popup__nav-right-ctrls">
                <span
                    onClick={handleGoTo}
                    title="Go to code"
                    className='crawler-inspector-popup__go-to-btn'>
                    {formatFileLine(location?.file, location?.line)}
                </span>
            </div>
        </div>
        <div className="crawler-inspector-popup__table-body-cell">
            <div style={{ display: 'flex', gap: '4px', flexDirection: 'column' }}>
                <Button
                    label="Browse"
                    theme={ButtonTheme.LightBlueWithBorder}
                    size={ButtonSize.Small}
                    onClick={handleGoToCorpusCb} />

                <Button
                    label="Restart"
                    theme={ButtonTheme.LightBlueWithBorder}
                    size={ButtonSize.Small}
                    onClick={handleRestart} />
                {isTaskCanBeStopped(statusMessage) &&
                    <Button
                        label="Stop"
                        theme={ButtonTheme.LightBlueWithBorder}
                        size={ButtonSize.Small}
                        onClick={handleStop} />}
                <Button
                    label="Delete"
                    theme={ButtonTheme.LightRedWithBorder}
                    size={ButtonSize.Small}
                    onClick={handleDelete} />
            </div>
        </div>
    </div>
}


function CrawlerInspector({ project, highlightLine, onClose, fontSize, codeTheme }) {

    const projectId = project?.projectId
    const {
        tasks,
        getTasks,
        isLoading,
        deleteTask,
        stopTask,
        restartTask,
        stopAllTasks,
        restartAllTasks,
        deleteAllTasks,
    } = useCrawlerTasks(projectId);
    const tableRef = useRef<HTMLDivElement>();
    const listRef = useRef<List>();
    const scrollRef = useRef(false);
    const [withScroll, setWithScroll] = useState(false);

    const setWithScrollDebounced = useCallback(_.debounce((scroll) => setWithScroll(scroll), 100), []);

    const [taskToDelete, setTaskToDelete] = useState<ICorpusTask>(null);
    const [taskToStop, setTaskToStop] = useState<ICorpusTask>(null);
    const [readyToStopAllTasks, setReadyToStopAllTasks] = useState<boolean>(null);
    const [readyToRestartAllTasks, setReadyToRestartAllTasks] = useState<boolean>(null);
    const [readyToDeleteAllTasks, setReadyToDeleteAllTasks] = useState<boolean>(null);
    const [taskToRestart, setTaskToRestart] = useState<ICorpusTask>(null);
    const [corpusToBrowse, setCorpusToBrowse] = useState<ICorpusTask>(null);

    const runningTasksUniqueKeys = (tasks || [])
        .filter(({ statusMessage }) => isTaskCanBeStopped(statusMessage))
        .map(t => t.uniqueKey);

    const tasksUniqueKeys = (tasks || []).map(t => t.uniqueKey);

    const handleTaskRestarting = useCallback((task) => {
        setTaskToRestart(task);
    }, []);

    const handleTaskStopping = useCallback((task) => {
        setTaskToStop(task);
    }, []);

    const handleStoppingAllTasks = useCallback(() => {
        setReadyToStopAllTasks(true);
    }, []);

    const handleTaskDeletion = useCallback((task) => {
        setTaskToDelete(task);
    }, []);

    const handleCorpusBrowse = useCallback((task) => {
        setCorpusToBrowse(task);
    }, []);

    const handleRestartingAllTasks = useCallback(() => {
        setReadyToRestartAllTasks(true);
    }, []);

    const handleDeletingAllTasks = useCallback(() => {
        setReadyToDeleteAllTasks(true);
    }, []);

    useClosePopup(onClose);

    return <PopupWrapper className={`crawler-inspector-popup font-${fontSize} ${codeTheme}`}>
        <PopupHeader
            title="Crawler Tasks"
            onCancel={onClose}>
            <PopupHeaderRightPart>
                <FlexContainer gap={6}>
                    <Button
                        label="Reload"
                        theme={ButtonTheme.LightBlueWithBorder}
                        size={ButtonSize.Medium}
                        onClick={getTasks}
                        icon={ButtonIcons.Refresh}
                    />
                    <Button
                        label="Restart All"
                        disabled={!(tasks?.length > 0)}
                        theme={ButtonTheme.LightBlueWithBorder}
                        size={ButtonSize.Medium}
                        onClick={handleRestartingAllTasks}
                    />
                    <Button
                        label="Delete All"
                        disabled={!(tasks?.length > 0)}
                        theme={ButtonTheme.LightRedWithBorder}
                        size={ButtonSize.Medium}
                        onClick={handleDeletingAllTasks}
                    />
                    <Button
                        label="Stop All"
                        disabled={runningTasksUniqueKeys?.length === 0}
                        theme={ButtonTheme.LightBlueWithBorder}
                        size={ButtonSize.Medium}
                        onClick={handleStoppingAllTasks}
                    />
                </FlexContainer>
            </PopupHeaderRightPart>
        </PopupHeader>
        <PopupBody className={isLoading ? 'disbaled' : ''}>
            {tasks?.length > 0 &&
                <div className="crawler-inspector-popup__table-holder">
                    <div className={`crawler-inspector-popup__table-header ${withScroll && 'with-scroll'}`}>
                        <div className="crawler-inspector-popup__table-header-cell">#</div>
                        <div className="crawler-inspector-popup__table-header-cell">Created</div>
                        <div className="crawler-inspector-popup__table-header-cell">Status</div>
                        <div className="crawler-inspector-popup__table-header-cell">Completed</div>
                        <div className="crawler-inspector-popup__table-header-cell">Duration</div>
                        <div className="crawler-inspector-popup__table-header-cell">Start count</div>
                        <div className="crawler-inspector-popup__table-header-cell">Code</div>
                        <div className="crawler-inspector-popup__table-header-cell"></div>
                    </div>
                    <div className={`crawler-inspector-popup__table-body ${withScroll && 'with-scroll'}`} ref={tableRef}>
                        <AutoSizer>
                            {({ height, width }) => {
                                return <List
                                    ref={listRef}
                                    width={width}
                                    height={height}
                                    rowCount={tasks?.length}
                                    rowHeight={ROW_HEIGHT}
                                    overscanRowCount={1}
                                    rowRenderer={(p) => {
                                        const { index, key, style, parent } = p;
                                        const task = tasks[index];

                                        if (index === 0) {
                                            scrollRef.current = parent.props.height < tasks?.length * ROW_HEIGHT;
                                            if (scrollRef.current !== withScroll) {
                                                setWithScrollDebounced((scrollRef.current));
                                            }
                                        }

                                        return <div key={key} style={style}>
                                            <TaskRow
                                                task={task}
                                                index={index}
                                                project={project}
                                                onClose={onClose}
                                                highlightLine={highlightLine}
                                                key={task.uniqueKey}
                                                onBrowse={handleCorpusBrowse}
                                                onDelete={handleTaskDeletion}
                                                onStop={handleTaskStopping}
                                                onRestart={handleTaskRestarting}
                                            />
                                        </div>;
                                    }}
                                />;
                            }}
                        </AutoSizer>

                    </div>
                </div>}
            {tasks?.length === 0 && <div style={{ padding: '40px', textAlign: 'center' }}>No tasks for this project</div>}
            {isLoading &&
                <Loader size={LoaderSize.Medium} theme={LoaderTheme.Dark} position='absolute' center={true} />}
            {taskToDelete &&
                <ConfirmPopup2
                    onClose={() => setTaskToDelete(null)}
                    okBtnLabel="Delete"
                    onOkBtnCb={() => {
                        setTaskToDelete(null)
                        deleteTask(projectId, taskToDelete.uniqueKey);
                    }}
                    title="Delete"
                    message={<div>Are you sure you want to delete this task?</div>}
                />
            }

            {taskToRestart &&
                <ConfirmPopup2
                    onClose={() => setTaskToRestart(null)}
                    okBtnLabel="Restart"
                    onOkBtnCb={() => {
                        setTaskToRestart(null)
                        restartTask(projectId, taskToRestart.uniqueKey);
                    }}
                    title="Restart"
                    message={<div>Are you sure you want to restart this task?</div>}
                />
            }


            {taskToStop &&
                <ConfirmPopup2
                    onClose={() => setTaskToStop(null)}
                    okBtnLabel="Stop"
                    onOkBtnCb={() => {
                        setTaskToStop(null)
                        stopTask(projectId, taskToStop.uniqueKey);
                    }}
                    title="Stop"
                    message={<div>Are you sure you want to stop this task?</div>}
                />
            }

            {readyToStopAllTasks &&
                <ConfirmPopup2
                    onClose={() => setReadyToStopAllTasks(null)}
                    okBtnLabel="Stop All"
                    onOkBtnCb={() => {
                        setReadyToStopAllTasks(null);
                        stopAllTasks(projectId, runningTasksUniqueKeys);
                    }}
                    title="Stop All"
                    message={<div>Are you sure you want to stop all tasks?</div>}
                />
            }

            {readyToRestartAllTasks &&
                <ConfirmPopup2
                    onClose={() => setReadyToRestartAllTasks(null)}
                    okBtnLabel="Restart All"
                    onOkBtnCb={() => {
                        setReadyToRestartAllTasks(null);
                        restartAllTasks(projectId, tasksUniqueKeys);
                    }}
                    title="Restart All"
                    message={<div>Are you sure you want to restart all tasks?</div>}
                />
            }

            {readyToDeleteAllTasks &&
                <ConfirmPopup2
                    onClose={() => setReadyToDeleteAllTasks(null)}
                    okBtnLabel="Delete All"
                    onOkBtnCb={() => {
                        setReadyToDeleteAllTasks(null);
                        deleteAllTasks(projectId, tasksUniqueKeys);
                    }}
                    title="Delete All"
                    message={<div>Are you sure you want to delete all tasks?</div>}
                />
            }

            {corpusToBrowse &&
                <TopLevelOverlay>
                    <CorpusPage
                        project={project}
                        projectId={project.projectId}
                        projectName={project.name}
                        corpusTitle={getCorpusTitleFromTask(corpusToBrowse)}
                        corpusId={corpusToBrowse.uniqueKey}
                        onClose={() => { setCorpusToBrowse(null); }}
                    />
                </TopLevelOverlay>}
        </PopupBody>
    </PopupWrapper>
}

export default CrawlerInspector;